/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * Native (Capacitor) OIDC redirect helpers.
 *
 * The problem this solves
 * -----------------------
 * Inside the Capacitor Android WebView the page origin is `https://localhost`
 * (androidScheme=https, no server.url override — see `capacitor.config.ts`).
 * oidc-client-ts therefore defaults the OIDC `redirect_uri` to
 * `https://localhost/authentication/callback`. The authorize step round-trips
 * through the system browser (Chrome Custom Tab), and when Authien redirects
 * back to `https://localhost/...` the SYSTEM browser — not the in-app WebView —
 * tries to resolve `localhost`, which on a physical phone has no server
 * listening → `ERR_CONNECTION_REFUSED`. The user never returns to the app.
 *
 * The fix
 * -------
 * On native, use the app's custom-scheme deeplink
 * `br.com.k4d.housien://authentication/callback` as the OIDC `redirect_uri`.
 * Android routes that scheme back into the app (via the manifest
 * `<intent-filter>` with `VIEW`/`BROWSABLE`), the `@capacitor/app`
 * `appUrlOpen` listener receives the full callback URL, and oidc-client-ts
 * completes the PKCE code→token exchange from it. The web flow is unchanged —
 * `isNativePlatform()` is false in a browser, so `getOidcRedirectOrigin()`
 * keeps returning `window.location.origin`.
 *
 * Security: this only changes WHERE the authorization code is delivered (an
 * app-owned scheme instead of a dead localhost origin). The full OIDC
 * validation — PKCE `code_verifier`, `state`, and `nonce` — still runs in
 * `signinCallback`. No validation is weakened.
 */

import { Capacitor } from '@capacitor/core';

/**
 * The app's registered custom URL scheme (matches `android:scheme` in the
 * manifest intent-filter and `custom_url_scheme` in `strings.xml`). Also the
 * iOS bundle-id-style scheme should v1 grow an iOS target.
 */
export const NATIVE_APP_SCHEME = 'br.com.k4d.housien';

/**
 * The native OIDC redirect target — an app-owned deeplink that Android routes
 * back into the app. This exact URI is registered on the Authien client
 * allowlist for every targeted stage.
 */
export const NATIVE_REDIRECT_URI = `${NATIVE_APP_SCHEME}://authentication/callback`;

/**
 * The native OIDC silent-renew target. Registered alongside the callback URI.
 */
export const NATIVE_SILENT_REDIRECT_URI = `${NATIVE_APP_SCHEME}://authentication/silent-renew`;

/**
 * Whether the app is running inside a Capacitor native shell (Android/iOS),
 * as opposed to a plain web browser.
 *
 * @returns True on a native platform, false on the web.
 */
export function isNativePlatform(): boolean {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    // Capacitor core is always present (it is a hard dep), but guard defensively
    // so a web unit test never throws here.
    return false;
  }
}

/**
 * Resolves the OIDC `redirect_uri` for the current platform.
 *
 * On native it returns the custom-scheme deeplink that Android routes back to
 * the app; on the web it returns `${window.location.origin}/authentication/callback`.
 *
 * @returns The fully-qualified redirect URI to pass to oidc-client-ts.
 */
export function resolveOidcRedirectUri(): string {
  if (isNativePlatform()) {
    return NATIVE_REDIRECT_URI;
  }
  return `${window.location.origin}/authentication/callback`;
}

/**
 * Resolves the OIDC silent-renew `redirect_uri` for the current platform.
 *
 * @returns The fully-qualified silent-renew redirect URI.
 */
export function resolveOidcSilentRedirectUri(): string {
  if (isNativePlatform()) {
    return NATIVE_SILENT_REDIRECT_URI;
  }
  return `${window.location.origin}/authentication/silent-renew`;
}

/**
 * Extracts the OIDC callback URL from a deeplink opened via `appUrlOpen`.
 *
 * Returns the URL only when it is this app's scheme AND it carries an OIDC
 * callback (an authorization `code` or an `error`), so unrelated deeplinks are
 * ignored. The returned string is the raw deeplink URL, suitable for
 * `UserManager.signinCallback(url)`.
 *
 * @param openUrl The `url` from a Capacitor `appUrlOpen` event.
 * @returns The callback URL to feed to oidc-client-ts, or null when the
 *          deeplink is not an OIDC callback for this app.
 */
export function extractOidcCallbackFromDeeplink(openUrl: string): string | null {
  if (typeof openUrl !== 'string' || !openUrl.startsWith(`${NATIVE_APP_SCHEME}://`)) {
    return null;
  }
  // Parse query AND fragment; some providers return params in the fragment.
  const [, afterScheme = ''] = openUrl.split('://');
  const hasCode = /[?&#]code=/.test(openUrl);
  const hasError = /[?&#]error=/.test(openUrl);
  const isCallbackPath = afterScheme.startsWith('authentication/callback');
  if (isCallbackPath && (hasCode || hasError)) {
    return openUrl;
  }
  return null;
}
